import { useAppNotificationManager } from ".";

import { translation } from "@/i18n";
import { nsApp } from "@/i18n/locales/app";

const useTranslation = translation.use(nsApp);

export type BulkActionToastMessages = {
  /** 処理件数を受け取りメッセージを返す。count=1 の場合は件数なし表記にするなど呼び出し側で制御。省略時は進行中 Toast を表示しない */
  inProgress?: (count: number) => string;
  success: (count: number) => string;
  error: (count: number) => string;
};

/**
 * 一括操作の実行状態を表す型。
 * - idle       : 待機中
 * - requested  : 実行リクエスト済み（処理未開始）。クリック時にスナップショットとして itemIds を設定する
 * - in_progress: 処理中
 *
 * phase が "idle" のときのみ "requested" へ遷移させることで再クリック時の二重実行を防ぐ。
 */
export type BulkActionPhase = "idle" | "requested" | "in_progress";
export type BulkActionRequest = {
  phase: BulkActionPhase;
  itemIds: string[];
};
export const createIdleRequest = (): BulkActionRequest => ({
  phase: "idle",
  itemIds: [],
});

/**
 * 進行中 Toast を表示しながら非同期処理を実行し、完了後に成功/失敗 Toast を表示する共通フック。
 *
 * - `runWithToast` : 1件以上のアイテムを順次処理し、成功したアイテムの結果と失敗した
 *                    アイテムの id 配列を返す。各操作の失敗は次の操作をキャンセルせずに続行する。
 *
 * @example
 * const { runWithToast } = useBulkActionToast({
 *   inProgress: (count) => count === 1 ? "ビューを複製中..." : `${count}件のビューを複製中...`,
 *   success:    (count) => count === 1 ? "ビューを複製しました" : `${count}件のビューを複製しました`,
 *   error:      (count) => count === 1 ? "ビューを複製できませんでした" : `${count}件のビューを複製できませんでした`,
 * });
 */
export const useBulkActionToast = (messages: BulkActionToastMessages) => {
  const { t } = useTranslation();
  const {
    showInProgressNotification,
    dismissInfoNotification,
    showSuccessNotification,
    showErrorNotification,
  } = useAppNotificationManager();

  /**
   * 成功したアイテムを1件ずつ取り消し、失敗分だけ再試行できる共通の「元に戻す」処理。
   * - 開始時に進行中 Toast を表示
   * - id毎に undoItem を実行し、失敗を収集
   * - 全成功なら成功 Toast、一部失敗ならエラー Toast を
   *   失敗分だけを対象にした再試行ボタン付きで表示
   * - 1件でも成功した場合、結果通知の前に onSomeUndoSucceeded の完了を待つ
   *   （onSomeUndoSucceeded の失敗時のハンドリングは呼び出し元の責務）
   */
  const runUndoWithToast = async <T, R>(
    targets: Array<{ item: T; result: R }>,
    undoItem: (result: R, item: T) => Promise<void>,
    onSomeUndoSucceeded?: () => Promise<void>
  ): Promise<void> => {
    const notifId = showInProgressNotification(t.app.undo.inProgress());

    const failedTargets: Array<{ item: T; result: R }> = [];
    let firstError: unknown;
    for (const target of targets) {
      try {
        await undoItem(target.result, target.item);
      } catch (error) {
        failedTargets.push(target);
        firstError ??= error;
      }
    }

    try {
      if (failedTargets.length < targets.length) {
        await onSomeUndoSucceeded?.();
      }
    } finally {
      dismissInfoNotification(notifId);
    }

    if (failedTargets.length === 0) {
      showSuccessNotification(t.app.undo.success());
      return;
    }

    showErrorNotification(t.app.undo.error(), firstError, {
      label: t.app.undo.actionLabel(),
      onClick: () => {
        void runUndoWithToast(
          failedTargets,
          undoItem,
          onSomeUndoSucceeded
        ).catch(() => {});
      },
    });
  };

  /**
   * 複数アイテムの非同期処理を進行中 Toast 付きで順次実行し、各処理の結果を返す。
   * 個別の失敗は後続の処理をキャンセルせず、最後に成功/失敗をまとめて通知する。
   * @returns 成功したアイテムの結果と失敗したアイテムの id 配列
   */
  const runWithToast = async <T extends { id: string }, R = void>(
    items: T[],
    fn: (item: T) => Promise<R>,
    options?: {
      /** 成功した各アイテムに対する取り消し処理。指定すると成功Toastに「元に戻す」ボタンが付く */
      undoItem?: (result: R, item: T) => Promise<void>;
      /** 取り消しが1件でも成功したときに、結果通知の前に待たれるコールバック */
      onSomeUndoSucceeded?: () => Promise<void>;
    }
  ): Promise<{
    /** 全アイテムが成功したか */
    allSuccess: boolean;
    successResults: Array<{ item: T; result: R }>;
    failedIds: string[];
  }> => {
    const count = items.length;
    const notifId = messages.inProgress
      ? showInProgressNotification(messages.inProgress(count))
      : undefined;

    const successResults: Array<{ item: T; result: R }> = [];
    const failedIds: string[] = [];
    let firstError: unknown;

    for (const item of items) {
      try {
        const result = await fn(item);
        successResults.push({ item, result });
      } catch (error) {
        failedIds.push(item.id);
        firstError ??= error;
      }
    }

    if (notifId !== undefined) {
      dismissInfoNotification(notifId);
    }

    if (successResults.length > 0) {
      const { undoItem, onSomeUndoSucceeded } = options ?? {};
      showSuccessNotification(
        messages.success(successResults.length),
        undoItem
          ? {
              label: t.app.undo.actionLabel(),
              onClick: () => {
                void runUndoWithToast(
                  successResults,
                  undoItem,
                  onSomeUndoSucceeded
                ).catch(() => {});
              },
            }
          : undefined
      );
    }
    if (failedIds.length > 0) {
      showErrorNotification(messages.error(failedIds.length), firstError);
    }

    return { allSuccess: failedIds.length === 0, successResults, failedIds };
  };

  return { runWithToast };
};
